import cubejs, { CubeApi } from '@cubejs-client/core';
import { LogManager } from '../../../common/port/log/log.manager.port';

export interface CubeConnectionPoolConfig {
  maxConnections?: number;
  idleTimeout?: number;
  connectionTimeout?: number;
  retryAttempts?: number;
  retryDelay?: number;
}

interface PooledConnection {
  client: CubeApi;
  token: string;
  lastUsed: number;
  isActive: boolean;
}

export class CubeConnectionPool {
  private connections: Map<string, PooledConnection[]> = new Map();
  private config: Required<CubeConnectionPoolConfig>;
  private cleanupInterval: NodeJS.Timeout | null = null;

  constructor(
    private readonly logManager: LogManager,
    private readonly endpoint: string,
    config: CubeConnectionPoolConfig = {}
  ) {
    this.config = {
      maxConnections: config.maxConnections || 10,
      idleTimeout: config.idleTimeout || 300000, // 5 minutes
      connectionTimeout: config.connectionTimeout || 30000, // 30 seconds
      retryAttempts: config.retryAttempts || 3,
      retryDelay: config.retryDelay || 1000, // 1 second
    };

    this.startCleanupInterval();
    this.logManager.debug('CubeConnectionPool initialized', this.config);
  }

  /**
   * Get a connection from the pool or create a new one
   */
  async getConnection(token: string): Promise<CubeApi> {
    const tokenKey = this.getTokenKey(token);
    const connections = this.connections.get(tokenKey) || [];
    
    // Find an available connection
    const availableConnection = connections.find(conn => 
      conn.isActive && this.isConnectionValid(conn)
    );

    if (availableConnection) {
      availableConnection.lastUsed = Date.now();
      this.logManager.debug(`Reusing connection for token: ${tokenKey}`);
      return availableConnection.client;
    }

    // Create new connection if under limit
    if (connections.length < this.config.maxConnections) {
      const newConnection = await this.createConnection(token);
      connections.push(newConnection);
      this.connections.set(tokenKey, connections);
      this.logManager.debug(`Created new connection for token: ${tokenKey}`);
      return newConnection.client;
    }

    // Wait for a connection to become available
    return this.waitForConnection(token);
  }

  /**
   * Release a connection back to the pool
   */
  releaseConnection(token: string, client: CubeApi): void {
    const tokenKey = this.getTokenKey(token);
    const connections = this.connections.get(tokenKey) || [];
    
    const connection = connections.find(conn => conn.client === client);
    if (connection) {
      connection.lastUsed = Date.now();
      this.logManager.debug(`Released connection for token: ${tokenKey}`);
    }
  }

  /**
   * Close all connections and cleanup
   */
  async close(): Promise<void> {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }

    const closePromises: Promise<void>[] = [];
    
    for (const [tokenKey, connections] of this.connections.entries()) {
      for (const connection of connections) {
        if (connection.isActive) {
          connection.isActive = false;
          // Note: cubejs client doesn't have an explicit close method
          // The connection will be garbage collected
        }
      }
    }

    this.connections.clear();
    this.logManager.debug('CubeConnectionPool closed');
  }

  /**
   * Get pool statistics
   */
  getStats(): Record<string, any> {
    const stats: Record<string, any> = {};
    
    for (const [tokenKey, connections] of this.connections.entries()) {
      const activeConnections = connections.filter(conn => conn.isActive).length;
      const totalConnections = connections.length;
      
      stats[tokenKey] = {
        active: activeConnections,
        total: totalConnections,
        max: this.config.maxConnections,
      };
    }

    return stats;
  }

  private async createConnection(token: string): Promise<PooledConnection> {
    try {
      const client = cubejs(token, {
        apiUrl: this.endpoint,
      });

      return {
        client,
        token,
        lastUsed: Date.now(),
        isActive: true,
      };
    } catch (error) {
      this.logManager.debug('Failed to create connection', error);
      throw error;
    }
  }

  private async waitForConnection(token: string): Promise<CubeApi> {
    const tokenKey = this.getTokenKey(token);
    const maxWaitTime = this.config.connectionTimeout;
    const startTime = Date.now();

    while (Date.now() - startTime < maxWaitTime) {
      const connections = this.connections.get(tokenKey) || [];
      const availableConnection = connections.find(conn => 
        conn.isActive && this.isConnectionValid(conn)
      );

      if (availableConnection) {
        availableConnection.lastUsed = Date.now();
        this.logManager.debug(`Waited and got connection for token: ${tokenKey}`);
        return availableConnection.client;
      }

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, this.config.retryDelay));
    }

    throw new Error(`Timeout waiting for connection for token: ${tokenKey}`);
  }

  private isConnectionValid(connection: PooledConnection): boolean {
    const now = Date.now();
    const timeSinceLastUsed = now - connection.lastUsed;
    return timeSinceLastUsed < this.config.idleTimeout;
  }

  private getTokenKey(token: string): string {
    // Create a hash of the token for privacy
    return Buffer.from(token).toString('base64').slice(0, 16);
  }

  private startCleanupInterval(): void {
    this.cleanupInterval = setInterval(() => {
      this.cleanupIdleConnections();
    }, this.config.idleTimeout / 2); // Cleanup every half of idle timeout
  }

  private cleanupIdleConnections(): void {
    const now = Date.now();
    let totalCleaned = 0;

    for (const [tokenKey, connections] of this.connections.entries()) {
      const activeConnections = connections.filter(conn => {
        const timeSinceLastUsed = now - conn.lastUsed;
        if (timeSinceLastUsed >= this.config.idleTimeout) {
          conn.isActive = false;
          totalCleaned++;
          return false;
        }
        return true;
      });

      if (activeConnections.length === 0) {
        this.connections.delete(tokenKey);
      } else {
        this.connections.set(tokenKey, activeConnections);
      }
    }

    if (totalCleaned > 0) {
      this.logManager.debug(`Cleaned up ${totalCleaned} idle connections`);
    }
  }
}
